import { useNavigate } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { Bell, Languages, LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useI18n } from '@/i18n/I18nProvider'
import {
  Avatar,
  AvatarFallback,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui'
import { LanguageRadioGroup } from '@/components/LanguageSwitcher'
import { LocaleFlag } from '@/components/LocaleFlag'
import { ThemeRadioGroup } from '@/components/ThemeProvider'
import { cn } from '@/lib/utils'

function initials(name?: string): string {
  if (!name) return '?'

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

export function UserAvatar({ className }: { className?: string }) {
  const { user } = useAuth()

  return (
    <Avatar className={cn('size-7 shrink-0', className)}>
      <AvatarFallback className="bg-primary/12 text-primary text-xs font-semibold dark:bg-primary/20">
        {initials(user?.name)}
      </AvatarFallback>
    </Avatar>
  )
}

/**
 * The account menu, one definition for every place it opens from.
 *
 * It used to be the only route to /profil and it lived in the sidebar footer,
 * which is `hidden lg:flex` — so on a phone there was **no route to the
 * profile at all**. It is now opened from the avatar in the header on every
 * screen size, and from the foot of the rail on a desktop. Both open this, so
 * the two cannot drift the next time an item is added.
 *
 * Language and theme moved in here from the header. They are set once and
 * left; a permanent slot each, on every screen, was two of the five icons that
 * made the header read as a toolbar of things nobody presses.
 *
 * The signed-in person's name and code head the menu. On a shared terminal
 * that is the first thing to check, and the avatar's initials alone cannot
 * tell two Amras apart.
 */
export function AccountMenuContent({
  side = 'bottom',
  align = 'end',
}: {
  side?: 'bottom' | 'right' | 'top'
  align?: 'start' | 'end'
}) {
  const { user, logout } = useAuth()
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor

  async function handleLogout() {
    await logout()
    navigate('/prijava')
  }

  return (
    <DropdownMenuContent
      side={side}
      align={align}
      sideOffset={8}
      className="supports-[backdrop-filter]:bg-popover/80 w-64 backdrop-blur-2xl backdrop-saturate-150"
    >
      {/*
        The Group wrapper is required, not decorative.

        This shadcn build is on Base UI, where DropdownMenuLabel is
        `Menu.GroupLabel` and throws "MenuGroupContext is missing" unless it has
        a `Menu.Group` ancestor. On Radix — which most shadcn snippets assume —
        the label stands alone quite happily. Same component name, different
        contract.
      */}
      <DropdownMenuGroup>
        <DropdownMenuLabel className="flex items-center gap-2.5 p-2">
          <UserAvatar className="size-8" />
          <span className="min-w-0">
            <span className="text-foreground block truncate text-sm font-medium">{user?.name}</span>
            <span className="block truncate text-xs font-normal">
              {[user?.employeeCode, user?.roleLabel].filter(Boolean).join(' · ')}
            </span>
          </span>
        </DropdownMenuLabel>
      </DropdownMenuGroup>

      <DropdownMenuSeparator />

      <DropdownMenuItem onClick={() => navigate('/profil')}>
        <UserRound />
        {t('account.myAccount')}
      </DropdownMenuItem>

      <DropdownMenuItem onClick={() => navigate('/obavijesti')}>
        <Bell />
        {t('notifications.title')}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Languages />
          <span className="flex-1">{t('language.label')}</span>
          {/* Which one is on, without opening the submenu to find out. */}
          <LocaleFlag locale={locale} />
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <LanguageRadioGroup />
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <ThemeIcon />
          <span className="flex-1">{t('theme.label')}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <ThemeRadioGroup />
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSeparator />

      {/* Its own item, marked destructive, one step from the name — never the
          row that shows who you are. That row used to *be* the sign-out button,
          so the only thing identifying you on screen ended your session. */}
      <DropdownMenuItem variant="destructive" onClick={handleLogout}>
        <LogOut />
        {t('account.signOut')}
      </DropdownMenuItem>
    </DropdownMenuContent>
  )
}
